import { KEYPAD_FAMILY } from './family';
import type { KeypadFamilyKey } from './family';
import { KEY_LAYOUT, KEY_LAYOUT_SPECS } from './layout';
import type { KeyLayoutKey } from './layout';

/**
 * The panels a technician can add.
 *
 * Every entry is a family (which decides the address rules) plus a layout (which
 * decides how many keys the panel has and where they sit). The two are separate
 * because a logic board and a standard keypad answer the same way but come with
 * different numbers of keys.
 */
export const KEYPAD_TYPE = {
  KEYPAD_1: 'keypad1',
  KEYPAD_2: 'keypad2',
  KEYPAD_3: 'keypad3',
  KEYPAD_4: 'keypad4',
  KEYPAD_6: 'keypad6',
  CORRIDOR: 'corridorPanel',
  KEYCARD: 'keycardReader',
  LOGIC_1: 'logicBoard1',
  LOGIC_2: 'logicBoard2',
  LOGIC_3: 'logicBoard3',
  LOGIC_4: 'logicBoard4',
} as const;

export type KeypadTypeKey = (typeof KEYPAD_TYPE)[keyof typeof KEYPAD_TYPE];

export interface KeypadTypeSpec {
  readonly label: string;
  readonly family: KeypadFamilyKey;
  readonly layout: KeyLayoutKey;
  /** What each key is called before the technician renames it, by key index. */
  readonly defaultKeyNames: readonly string[];
}

/** `Key 1`…`Key n`, the reference app's own fallback names. */
const numbered = (prefix: string, count: number): readonly string[] =>
  Array.from({ length: count }, (_unused, index) => `${prefix} ${index + 1}`);

/**
 * The corridor panel's keys are fixed by the reference app: three service keys
 * that share an address, and the bell, which answers on its own.
 */
const CORRIDOR_KEY_NAMES = ['MMR', 'Occupied', 'Privacy', 'Bell'] as const;

export const KEYPAD_TYPE_SPECS = {
  [KEYPAD_TYPE.KEYPAD_1]: {
    label: '1-key keypad',
    family: KEYPAD_FAMILY.STANDARD,
    layout: KEY_LAYOUT.KEYS_1,
    defaultKeyNames: numbered('Key', 1),
  },
  [KEYPAD_TYPE.KEYPAD_2]: {
    label: '2-key keypad',
    family: KEYPAD_FAMILY.STANDARD,
    layout: KEY_LAYOUT.KEYS_2,
    defaultKeyNames: numbered('Key', 2),
  },
  [KEYPAD_TYPE.KEYPAD_3]: {
    label: '3-key keypad',
    family: KEYPAD_FAMILY.STANDARD,
    layout: KEY_LAYOUT.KEYS_3,
    defaultKeyNames: numbered('Key', 3),
  },
  [KEYPAD_TYPE.KEYPAD_4]: {
    label: '4-key keypad',
    family: KEYPAD_FAMILY.STANDARD,
    layout: KEY_LAYOUT.KEYS_4,
    defaultKeyNames: numbered('Key', 4),
  },
  [KEYPAD_TYPE.KEYPAD_6]: {
    label: '6-key keypad',
    family: KEYPAD_FAMILY.STANDARD,
    layout: KEY_LAYOUT.KEYS_6,
    defaultKeyNames: numbered('Key', 6),
  },
  [KEYPAD_TYPE.CORRIDOR]: {
    label: 'Corridor panel',
    family: KEYPAD_FAMILY.CORRIDOR,
    layout: KEY_LAYOUT.CORRIDOR,
    defaultKeyNames: CORRIDOR_KEY_NAMES,
  },
  [KEYPAD_TYPE.KEYCARD]: {
    label: 'Keycard reader',
    family: KEYPAD_FAMILY.KEYCARD,
    layout: KEY_LAYOUT.KEYS_1,
    defaultKeyNames: ['Card'],
  },
  [KEYPAD_TYPE.LOGIC_1]: {
    label: '1-input logic board',
    family: KEYPAD_FAMILY.LOGIC_BOARD,
    layout: KEY_LAYOUT.KEYS_1,
    defaultKeyNames: numbered('Input', 1),
  },
  [KEYPAD_TYPE.LOGIC_2]: {
    label: '2-input logic board',
    family: KEYPAD_FAMILY.LOGIC_BOARD,
    layout: KEY_LAYOUT.KEYS_2,
    defaultKeyNames: numbered('Input', 2),
  },
  [KEYPAD_TYPE.LOGIC_3]: {
    label: '3-input logic board',
    family: KEYPAD_FAMILY.LOGIC_BOARD,
    layout: KEY_LAYOUT.KEYS_3,
    defaultKeyNames: numbered('Input', 3),
  },
  [KEYPAD_TYPE.LOGIC_4]: {
    label: '4-input logic board',
    family: KEYPAD_FAMILY.LOGIC_BOARD,
    layout: KEY_LAYOUT.KEYS_4,
    defaultKeyNames: numbered('Input', 4),
  },
} as const satisfies Record<KeypadTypeKey, KeypadTypeSpec>;

export const KEYPAD_TYPE_KEYS = [
  KEYPAD_TYPE.KEYPAD_1,
  KEYPAD_TYPE.KEYPAD_2,
  KEYPAD_TYPE.KEYPAD_3,
  KEYPAD_TYPE.KEYPAD_4,
  KEYPAD_TYPE.KEYPAD_6,
  KEYPAD_TYPE.CORRIDOR,
  KEYPAD_TYPE.KEYCARD,
  KEYPAD_TYPE.LOGIC_1,
  KEYPAD_TYPE.LOGIC_2,
  KEYPAD_TYPE.LOGIC_3,
  KEYPAD_TYPE.LOGIC_4,
] as const satisfies readonly KeypadTypeKey[];

/** The family whose address rules a type follows. */
export const familyOfType = (type: KeypadTypeKey): KeypadFamilyKey =>
  KEYPAD_TYPE_SPECS[type].family;

/** The layout a type's keys follow. */
export const layoutOfType = (type: KeypadTypeKey): KeyLayoutKey => KEYPAD_TYPE_SPECS[type].layout;

/** How many keys a type carries, read from its layout. */
export const keyCountOfType = (type: KeypadTypeKey): number =>
  KEY_LAYOUT_SPECS[layoutOfType(type)].cells.length;

/** The name a key of this type falls back to. */
export const defaultKeyNameOf = (type: KeypadTypeKey, index: number): string =>
  KEYPAD_TYPE_SPECS[type].defaultKeyNames[index] ?? '';

/** The types of one family, in catalog order — what the picker offers. */
export const typesOfFamily = (family: KeypadFamilyKey): readonly KeypadTypeKey[] =>
  KEYPAD_TYPE_KEYS.filter((type) => KEYPAD_TYPE_SPECS[type].family === family);
